// @flow
import OrderProcessingApi from "../api/OrderProcessingApi";
import {ThunkAction} from "@reduxjs/toolkit";
import type {Order, OrderCustomerMail, OrderItemsResponse} from "../models/Order";
import type {PaymentFacilitySessionResponse, PaymentMethod} from "../api/PaymentApi";
import PaymentApi from "../api/PaymentApi";
import type {ApplicationState} from "../reducers";
import {CHECKOUT_PAGE_STATUS} from "../components/checkout/CheckoutPage";
import {isPayPalProvider, PAYMENT_METHODS, PAYMENT_PROVIDER} from "../models/PaymentRequests";
import i18next from "../i18next";

import {
    createOrderFailed,
    createOrderRequested,
    paymentProviderFailureSet,
    paymentProviderRequestSet, paymentProviderReset,
    paymentProviderSuccessSet,
    verifyPaymentFailed,
    itemMarkedSoldOut,
    cartDrawerOpened
} from "../reducers/cartReducer";
import {
    addressSet,
    checkoutPageStepSet, expressPaymentInitiated,
    orderDetailsCleared, orderStateReset, orderStateSet,
    orderSuccessCreated, paymentAuthorizationFailed, paymentReset,
    paymentSet, paymentSuccessVerified, paymentVerified, shopDonePageClosed
} from "../reducers/orderReducer";

const orderProcessingApi = new OrderProcessingApi();
const paymentApi = new PaymentApi();

export const prepareOrder = (getState: () => ApplicationState, includeCustomerAddress: boolean): Order => {
	const cartItems = getState().cart.items;
	const pickupLocation = getState().location.pickupLocation ? {index: getState().location.pickupLocation.cfgKey} : null;
	const slyCustomer = getState().coupon.sly;
	const slyToken = getState().coupon.slyToken;
	const orderItems = cartItems.map(item => {
		return {
			product: {
				dealerId: getState().config.dealerId,
				productNo: item.product.articleNumber,
				purchaseOrderNo: item.product.id
			},
			amount: {
				amount: item.amount,
				unit: {
					code: "piece"
				}
			},
			shippingMethod: {
				code: item.shippingMethod
			}
		};
	});
	const customerLanguage = getState().config.moduleLanguage || "en";
	const myCoupon = (getState().coupon.coupon && getState().coupon.coupon.coupon) ? getState().coupon.coupon.coupon : null;
	const couponCode = (myCoupon && myCoupon.code) ? myCoupon.code : undefined;

	let bonusRedeemer = null;
	if (slyCustomer) {
		bonusRedeemer = {
			provider: "SLY",
			payload: slyToken
		};
	}

	if (includeCustomerAddress) {
		const customerMail: OrderCustomerMail = {
			mailTo: getState().order.adress.email
		};
		const billingAddress = {
			firstName: getState().order.adress.name,
			name: getState().order.adress.familyname,
			street: getState().order.adress.street,
			city: getState().order.adress.city,
			postalCode: getState().order.adress.postalCode,
			countryCode: getState().order.adress.countryCode
		};
		let shippingAddress = billingAddress;
		if (getState().order.adress.shippingAdress) {
			shippingAddress = {
				firstName: getState().order.adress.shippingName,
				name: getState().order.adress.shippingFamilyname,
				street: getState().order.adress.shippingStreet,
				city: getState().order.adress.shippingCity,
				postalCode: getState().order.adress.shippingPostalCode,
				countryCode: getState().order.adress.shippingCountryCode
			};
		}

		const sps = getState().coupon.sps;
		let comment = getState().order.adress.comment;

		if (sps) {
			comment += `\n\n${i18next.t("cart.coupon.spsCustomerNo")}: ${sps.spsCustomerNo}`;
		}

		const sly = getState().coupon.sly;
		if (sly) {
			comment += `\n\n${i18next.t("cart.coupon.slyCustomerNo")}: ${sly.cardnumber}`;
		}

		return {
			billingAddress,
			customerLanguage,
			customerMail,
			assembler: pickupLocation,
			dealerNo: {
				index: getState().config.dealerId
			},
			shippingAddress,
			items: orderItems,
			couponCode: couponCode,
			customerPhone: getState().order.adress.phone,
            licensePlate: getState().order.adress.licensePlate,
			vin: getState().order.adress.vin,
			comment: comment,
			bonusRedeemer
		};
	}

	return {
		customerLanguage,
		assembler: pickupLocation,
		dealerNo: {
			index: getState().config.dealerId
		},
		items: orderItems,
		couponCode: couponCode,
		bonusRedeemer
	};
};

// create order always creates a new, initial payment (with unknown payment method) in the backend
// so we have to reset the already chosen payment!
export function createOrder(): ThunkAction {
	return async (dispatch, getState: () => ApplicationState) => {
		dispatch(createOrderRequested());
        dispatch(orderStateReset());

		const order = prepareOrder(getState, true);
		orderProcessingApi.createOrder(order)
			.then((response: OrderItemsResponse) => {
                dispatch(paymentReset());
                dispatch(paymentProviderReset());
                dispatch(orderSuccessCreated(response));
			})
			.catch(() => {
                dispatch(createOrderFailed())
            });
	};
}

export const onPayPalExpressOrderCreated = (order: Order) => {
    return dispatch => {
        dispatch(expressPaymentInitiated());
        dispatch(orderSuccessCreated(order));
    }
};

export const notifyPayPalPaymentSuccessful = () => {
	return (dispatch, getState: () => ApplicationState) => {
		paymentApi.notifyPaypalPaymentSuccessful(getState().order.order.orderNo)
			.then(() => {
				dispatch(onVerifyPaymentSuccess());
            })
			.catch((err) => {
                console.error("paymentApi.notifyPaypalPaymentSuccessful error: ", err);
				dispatch(verifyPaymentFailed());
			});
	};
};

export const getOrderState = (orderNo: string) => {
    return dispatch => {
        orderProcessingApi.getOrderState(orderNo)
            .then(response => {
                dispatch(orderStateSet(response));
            })
            .catch(err => {
                console.error("Error getting order state:", err);
            });
    };
};

export const notifyPayPalPaymentCancelled = () => {
    return (dispatch, getState: () => ApplicationState) => {
        paymentApi.notifyPaypalPaymentCancelled(getState().order.order.orderNo)
            .then(() => {
            })
            .catch(err => {
                console.error("Error getting order state:", err);
            });
    };
};

export const onVerifyPaymentSuccess = () => {
	return dispatch => {
		if (window["gtag"]) {
			const gtagReportConversion = url => {
				const callback = function () {
					if (typeof (url) !== "undefined") {
						window.location = url;
					}
				};
				window["gtag"]("event", "conversion", {
					"send_to": "AW-975834537/PyzrCJLisPsBEKmbqNED",
					"transaction_id": "",
					"value": 1.0,
					"currency": "EUR",
					"event_callback": callback
				});
				return false;
			};
			gtagReportConversion();
		}

        dispatch(paymentVerified());
    };
};

export const assignPaymentToOrder = (method: PaymentMethod) => {
	return (dispatch, getState: () => ApplicationState) => {
		dispatch(paymentProviderRequestSet());
		const paymentApi = new PaymentApi();

		paymentApi.assignPaymentForOrder(getState().order.order.orderNo, method)
			.then((response: PaymentFacilitySessionResponse) => {
                dispatch(setPaymentToOrder(response, method.paymentMethod))
            })
			.catch(() => {
				// every PayPal method is assigned through the dealer's same PayPal credentials, so they fail alike
				const failedMethods = isPayPalProvider(method.paymentProvider)
					? getState().cart.paymentMethods
						.filter(payment => isPayPalProvider(payment.paymentProvider))
						.map(payment => payment.paymentMethod)
					: [method.paymentMethod];
				dispatch(paymentProviderFailureSet({
					paymentMethods: failedMethods,
				}));
			});
	};
};

export const setPaymentError = (method: string) => {
	return dispatch => {
		dispatch(verifyPaymentFailed());
        dispatch(paymentProviderFailureSet({
            paymentMethods: [method],
        }));
	}
}

export const onPayPalExpressPaymentAssigned = (response: PaymentFacilitySessionResponse) => {
    return dispatch => {
        dispatch(setPaymentToOrder(response, PAYMENT_METHODS.PAYPAL_EXPRESS));
        dispatch(paymentSet({
            paymentProvider: response.session.paymentProvider,
            paymentReference: response.session.account.referenceId
        }));
    }
};

export const acceptOrder = () => {
	return (dispatch, getState: () => ApplicationState) => {
		orderProcessingApi.acceptOrder(getState().order.order.orderNo)
			.then(_res => {
                dispatch(paymentSuccessVerified());
                dispatch(clearOrderDetails());
                dispatch(checkoutPageStepSet(CHECKOUT_PAGE_STATUS.FINISH));
            })
			.catch(err => {
				const errorData = err && err.response ? err.response.data : null;
				// an item sold out between adding to cart and order acceptance: mark it and open the
				// cart for the customer to remove it - the cart says why; the checkout starts over at the address. Discriminate on the body code, not the
				// status (409 is also used for "not authorized"). One sold-out item surfaces per attempt.
				if (errorData && errorData.error === "PRODUCT_SOLD_OUT" && errorData.productNo) {
					dispatch(itemMarkedSoldOut({productNo: errorData.productNo}));
					dispatch(checkoutPageStepSet(CHECKOUT_PAGE_STATUS.ADRESS));
					dispatch(cartDrawerOpened());
					return;
				}
				console.error("Error on submitting Order!", err);
				dispatch(verifyPaymentFailed());
                dispatch(paymentAuthorizationFailed());
			});
	};
};

export const saveAdress = address => {
	return addressSet(address);
};

export const clearOrderDetails = () => {
	return orderDetailsCleared();
};

export const orderFinishCheckout = () => {
	return shopDonePageClosed();
};

export const resetOrderState = () => {
	return orderStateReset();
};

// if total of order is 0 (fully paid by coupon)
export const setFreePayment = () => {
    return dispatch => {
        dispatch(paymentSet({paymentProvider: PAYMENT_PROVIDER.FREE}));
        dispatch(paymentProviderSuccessSet({paymentProvider: PAYMENT_PROVIDER.FREE, paymentMethod: PAYMENT_METHODS.FREE}));
        dispatch(onVerifyPaymentSuccess());
        dispatch(checkoutPageStepSet(CHECKOUT_PAGE_STATUS.OVERVIEW));
    }
};

const setPaymentToOrder = (response: PaymentFacilitySessionResponse, paymentMethod: string) => {
    return dispatch => {
        dispatch(paymentProviderSuccessSet({
            paymentProvider: (response.session.paymentProvider || "unset").toLowerCase(),
            paymentMethod,
            account: response.session.account,
        }));
        dispatch(paymentSet({
            paymentProvider: response.session.paymentProvider,
            paymentReference: response.session.account.referenceId,
        }));
    }
};

